/* eslint-disable max-lines */
// deps
import React, { ReactElement, useCallback, useState, useMemo } from "react";

// internal-deps
import { captureError, GenericCroutonProps } from "@swiggy-private/core-ui";

// components
import AddNewCardDetails from "./components/Details";
import { SecureCardInfo } from "@payment-ui/components/secure-card-info";
import SelectionCard from "./components/SelectionCard";
import ProceedButton from "./components/ProceedButton";

// interfaces
import {
    AddNewCardCommonProps,
    ADD_NEW_CARD_PROCEED_ACTION_STATUS,
    CardPaymentGroupActions,
    CardPaymentMethodData,
    ICardOffer,
    ICardFingerprint,
} from "@payment-ui/interfaces";

// helpers
import { getCardValidator, getMonthAndYearFromExpiry, isValidCard } from "@payment-ui/helpers/Card";
import { SimpleCardValidator } from "../../types/Juspay";
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import { isEmpty } from "@swiggy-private/core-ui";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { DEFAULT_ADD_NEW_CARD_ERROR_STATE } from "@payment-ui/constants/Card";
import { CROUTON_CLEAR_STATE, EMI_SUPPORTED_GATEWAY_ID } from "./constants";

// hooks
import { useCardHalfCard } from "@payment-ui/hooks/useCardHalfCardActions";

// styles
import styles from "./AddNewCard.v4.scss";

const NON_SUPPORTED_CARD_MESSAGE = "Sodexo Card are not supported for non food flows yet";

export type AddNewCardProps = AddNewCardCommonProps & {
    methodData: CardPaymentMethodData;
    onPay: CardPaymentGroupActions["onPay"];
    paymentInProgress: boolean;
    cardOffer?: ICardOffer;
    cardFingerPrintData?: ICardFingerprint | null;
    apiInProgress?: boolean;
    isCardBankSameAsSelectedEmiBank?: boolean | null;
    isCardDiscountStripEnabled?: boolean;
    emiData?: {
        tenure?: number;
        bankCode?: string;
        emiBank?: string;
        emiType?: string;
        txnEmiType?: string;
        bankName?: string;
    };
    showCrouton?: (croutonState: GenericCroutonProps) => void;
};

interface CardDetails {
    cardNumber: string;
    cvv: string;
    name: string;
    expiry: string;
    nickName: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function AddNewCard(props: AddNewCardProps): ReactElement {
    const [details, setDetails] = useState<CardDetails>({
        cardNumber: "",
        cvv: "",
        name: "",
        expiry: "",
        nickName: "",
    });

    const {
        onPay,
        paymentInProgress,
        onSecureCardInfoView,
        onSecureCardInfoClickAction,
        onCardNumberChange,
        cardBrandDetails,
        onKnowMoreTokenizationClickAction,
        onProceedToPayClick,
        onSaveCardClick,
        onProceedToPayClickAction,
        cardOffer,
        onCardNumberBlur,
        cardFingerPrintData,
        apiInProgress = false,
        skipClientCardValidation,
        isCardBankSameAsSelectedEmiBank,
        emiData,
        showCrouton,
        isCardDiscountStripEnabled = false,
        saveCardCtaHelperText,
    } = props;

    //Hook for Half card related actions
    const { showSecureCardInfo, onSecureCardInfoClick, closeSecureCardInfo, showSecureCardHalfCardWithActions } =
        useCardHalfCard({
            onSecureCardInfoClickAction,
            onKnowMoreTokenizationClickAction,
        });

    const [ctaEnabled, setCtaEnabled] = useState(false);
    const [errors, setErrors] = useState(DEFAULT_ADD_NEW_CARD_ERROR_STATE);

    const [secureCard, setSecureCard] = useState(false);

    const handleSecureCardClick = useCallback((): void => {
        typeof onSaveCardClick === "function" && onSaveCardClick(!secureCard);
        setSecureCard(!secureCard);
    }, [secureCard, onSaveCardClick]);

    const isValidCardDetails = useCallback(
        (validator: SimpleCardValidator): boolean => {
            const errors = isValidCard(validator, details);
            setErrors(errors);
            const isValid = !errors.cardNumber && !errors.cvv && !errors.expiry && !errors.name;
            setCtaEnabled(ctaEnabled && isValid);
            return isValid;
        },
        [details, ctaEnabled],
    );

    const hideCrouton = useCallback(() => {
        showCrouton?.(CROUTON_CLEAR_STATE);
    }, [showCrouton]);

    const handleCardNumberBlur = useCallback(
        (cardNumber: string) => {
            onCardNumberBlur?.(cardNumber);
        },
        [onCardNumberBlur],
    );

    // other actions
    const proceedToPay = useCallback(
        ({ brand, month, saveCard, year }: { saveCard: boolean; month: string; year: string; brand: string }): void => {
            const nickName = details.nickName.trim();
            const cardFingerPrintTokenData = cardFingerPrintData
                ? {
                      cardFingerprint: cardFingerPrintData?.card_fingerprint,
                      par: cardFingerPrintData?.par,
                  }
                : null;

            const extraEmiParams = {
                emiBank: emiData?.emiBank,
                isEmi: true,
                supportedGatewayId: EMI_SUPPORTED_GATEWAY_ID,
                emiType: emiData?.emiType,
                bankCode: emiData?.bankCode,
                emiTenure: emiData?.tenure,
                txnEmiType: emiData?.txnEmiType,
            };

            onPay(
                {
                    ...props.methodData,
                    payableAmount: cardOffer?.payableAmount,
                    bin: cardBrandDetails.cardBin || "",
                    meta: {
                        ...props.methodData.meta,
                        token: cardFingerPrintTokenData,
                        tokenizeSupport: cardBrandDetails.tokenizeSupport || false,
                    },
                },
                {
                    ...details,
                    saveCard,
                    month,
                    year,
                    brand,
                    nickName,
                    cardBrand: cardBrandDetails.cardBrand || "",
                    cardBin: cardBrandDetails.cardBin || "",
                    ...(isCardBankSameAsSelectedEmiBank === true ? extraEmiParams : {}),
                },
                { isRewardApplicable: cardOffer?.rewardApplicable || false },
            );
        },
        [
            details,
            cardFingerPrintData,
            emiData?.emiBank,
            emiData?.emiType,
            emiData?.bankCode,
            emiData?.tenure,
            emiData?.txnEmiType,
            onPay,
            props.methodData,
            cardOffer?.payableAmount,
            cardOffer?.rewardApplicable,
            cardBrandDetails.cardBin,
            cardBrandDetails.tokenizeSupport,
            cardBrandDetails.cardBrand,
            isCardBankSameAsSelectedEmiBank,
        ],
    );

    const onPayClick = useCallback(
        async (secureCardVal: boolean): Promise<void> => {
            setSecureCard(secureCardVal);
            // Extra context so the NR proceed-to-pay event can distinguish blocked states per card brand.
            const proceedMeta = { cardBrand: cardBrandDetails.cardBrand, skipClientCardValidation };
            // adding try catch to handle and capture simple card validator module bugs
            try {
                if (paymentInProgress) {
                    onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.PAYMENT_IN_PROGRESS, proceedMeta);
                    return;
                }
                const validator = getCardValidator(details.cardNumber.trim());

                if (!skipClientCardValidation && !isValidCardDetails(validator)) {
                    onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.INVALID_CARD_DETAILS, proceedMeta);
                    return;
                }
                const cardDetails = validator.getCardDetails();
                const { month, year } = getMonthAndYearFromExpiry(details.expiry);
                const brand = cardDetails.card_type.toUpperCase();

                onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.PROCEED_TO_PAY, proceedMeta);
                proceedToPay({ saveCard: secureCardVal, month, year, brand });
            } catch (e) {
                onProceedToPayClick(ADD_NEW_CARD_PROCEED_ACTION_STATUS.FAILED_TO_PROCEED, proceedMeta);
                captureError(e);
            }
        },
        [
            details.cardNumber,
            details.expiry,
            isValidCardDetails,
            onProceedToPayClick,
            paymentInProgress,
            proceedToPay,
            skipClientCardValidation,
            cardBrandDetails.cardBrand,
        ],
    );

    const resetError = useCallback((): void => setErrors(DEFAULT_ADD_NEW_CARD_ERROR_STATE), []);

    const updateDetails = useCallback(
        (details: CardDetails): void => {
            const { nickName, ...otherDetails } = details;
            const enableCTA = Object.values(otherDetails).every((field) => field !== "");

            if (isCardBankSameAsSelectedEmiBank === true) {
                hideCrouton();
            }

            setCtaEnabled(enableCTA);
            setDetails(details);

            setErrors(DEFAULT_ADD_NEW_CARD_ERROR_STATE);
        },
        [hideCrouton, isCardBankSameAsSelectedEmiBank],
    );

    const _ctaEnabled = useMemo(() => {
        const isCardBankValidationNeededAndValid =
            typeof isCardBankSameAsSelectedEmiBank === "boolean" ? isCardBankSameAsSelectedEmiBank : true; // default to true since emi check is not needed

        return ctaEnabled && !apiInProgress && isCardBankValidationNeededAndValid;
    }, [ctaEnabled, apiInProgress, isCardBankSameAsSelectedEmiBank]);

    const isCardDisabled = useMemo(
        () =>
            !isEmpty(cardBrandDetails.disableForTenant) &&
            !isEmpty(cardBrandDetails.disableForTenantReason) &&
            cardBrandDetails.disableForTenant,
        [cardBrandDetails.disableForTenant, cardBrandDetails.disableForTenantReason],
    );

    const showCardNumberErrorMessage = useMemo(() => {
        return isCardDisabled || !cardBrandDetails?.enabled;
    }, [isCardDisabled, cardBrandDetails?.enabled]);

    const cardNumberErrorMessage = useMemo(() => {
        if (!cardBrandDetails?.enabled) {
            return cardBrandDetails?.disableReason || "";
        }
        if (isCardDisabled) {
            return cardBrandDetails.disableForTenantReason || NON_SUPPORTED_CARD_MESSAGE;
        }

        return undefined;
    }, [
        cardBrandDetails.disableForTenantReason,
        cardBrandDetails?.disableReason,
        isCardDisabled,
        cardBrandDetails?.enabled,
    ]);

    const detailsProps = useMemo(
        () => ({
            details,
            errors,
            resetError,
            updateDetails,
            cardBrandDetails,
            onCardNumberChange,
            cardNumberErrorMessage,
            onCardNumberBlur: handleCardNumberBlur,
            rewardApplicable: cardOffer?.rewardApplicable,
            payableAmount: cardOffer?.payableAmount,
        }),
        [
            details,
            errors,
            resetError,
            updateDetails,
            cardBrandDetails,
            onCardNumberChange,
            cardNumberErrorMessage,
            handleCardNumberBlur,
            cardOffer?.rewardApplicable,
            cardOffer?.payableAmount,
        ],
    );
    return (
        <>
            <div className={styles.newCard}>
                <div>
                    <AddNewCardDetails {...detailsProps} />
                    <div className={styles.newCardSelectionCardWrapper}>
                        <SelectionCard
                            title={TEXTS.SECURE_CARD}
                            titleLink={props.juspayTokenizationMeta && TEXTS.WHY_IS_IT_IMP}
                            selected={secureCard}
                            onClick={handleSecureCardClick}
                            onInfoClick={onSecureCardInfoClick}
                        />
                    </div>
                </div>
                {showSecureCardInfo.show && props.juspayTokenizationMeta && (
                    <SecureCardInfo
                        data={props.juspayTokenizationMeta}
                        {...showSecureCardInfo.actions}
                        onClose={closeSecureCardInfo}
                        cardBrand={capitalizeFirstLetter(cardBrandDetails.cardBrand || "")}
                        onLoad={onSecureCardInfoView}
                    />
                )}
            </div>
            <ProceedButton
                secureCard={secureCard}
                inProgress={apiInProgress}
                juspayTokenizationMeta={!!props.juspayTokenizationMeta}
                ctaEnabled={_ctaEnabled}
                showCardNumberErrorMessage={showCardNumberErrorMessage}
                onPayClick={onPayClick}
                showSecureCardHalfCardWithActions={showSecureCardHalfCardWithActions}
                onProceedToPayClickAction={onProceedToPayClickAction}
                payableAmount={cardOffer?.payableAmount}
                rewardApplicable={cardOffer?.rewardApplicable}
                discountText={isCardDiscountStripEnabled ? cardOffer?.discountTitle : undefined}
                saveCardCtaHelperText={saveCardCtaHelperText}
            />
        </>
    );
}
